import "./OCRRatedAgeModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { useMemo, useState } from "react";
import { addProcessingRatedAgeOCRJob } from "reducers/rated-age-ocr";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import { useGetDocumentsQuery } from "services/documents-api";
import { useUploadOCRRatedAgeMutation } from "services/rre-profile-ingestion-api";
import {
  OCR_RATED_AGE_JOB_TYPE,
  RATED_AGE_JOB_POLL_INTERVAL_MS,
  isBackgroundJobProcessing,
} from "utils/backgroundJobStatus";
import { getDateFromISOStringDatePart } from "utils/date-utils";
import { useDispatch } from "utils/redux";

import { KaboodleToaster } from "../common";
import KaboodleModal from "./KaboodleModal";

type OCRRatedAgeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  fileId?: string | number;
  msaId?: string | number;
  costProjectionId?: string | number;
};

const OCRRatedAgeModal = ({
  isOpen,
  onClose,
  fileId,
  msaId,
  costProjectionId,
}: OCRRatedAgeModalProps) => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const [uploadOCRRatedAge, { isLoading }] = useUploadOCRRatedAgeMutation();
  const fileIdString = fileId?.toString() || "";
  const hasTarget = Boolean(msaId || costProjectionId);

  // Blocks a second submission while one is already running, for any
  // user viewing this file, not just whoever started it.
  const jobFilters = msaId
    ? { msa_id: msaId }
    : costProjectionId
      ? { cost_projection_id: costProjectionId }
      : undefined;

  const { data: jobsResponse } = useGetBackgroundJobsQuery(
    { filters: jobFilters },
    {
      skip: !jobFilters || !isOpen,
      pollingInterval: RATED_AGE_JOB_POLL_INTERVAL_MS,
    },
  );

  const latestJob = jobsResponse?.results?.find(
    (job) => job.job_type === OCR_RATED_AGE_JOB_TYPE,
  );
  const isAlreadyProcessing =
    !!latestJob && isBackgroundJobProcessing(latestJob.status);

  const { data: documentsData, isLoading: isDocumentsLoading } =
    useGetDocumentsQuery(
      {
        fileId: fileIdString,
        sortBy: "-created",
        // Filtered server-side, so this never needs the 1000-row limit
        // other OCR pickers use for their unfiltered document lists.
        filters: { type: "RATED_AGE" },
        limit: "100",
        offset: "0",
      },
      {
        skip: !fileIdString || !isOpen,
        refetchOnMountOrArgChange: true,
      },
    );

  const documents = documentsData?.results || [];

  const filteredDocuments = useMemo(
    () =>
      documents.filter(
        // A still-uploading drawer row has no size yet, and picking
        // one would 404 rather than submit.
        (doc) => doc.file_size_in_bytes != null,
      ),
    [documents],
  );

  const handleUpload = async () => {
    if (!selectedDocumentId) {
      KaboodleToaster.error("Please select a document to process");
      return;
    }

    if (!hasTarget) {
      // Submit is already disabled when !hasTarget, this is just a
      // safety net in case handleUpload ever gets called another way.
      KaboodleToaster.error(
        "Could not determine the MSA or Cost Projection. Please try again from a rated age page.",
      );
      return;
    }

    if (isAlreadyProcessing) {
      // Same safety net as !hasTarget above, Submit is already disabled.
      KaboodleToaster.error(
        "A rated age response is already being processed for this file.",
      );
      return;
    }

    // The early returns below are all inside this try block, so `finally`
    // still resets isSubmitting for every one of them.
    try {
      setIsSubmitting(true);

      // The document is already stored server-side, so the backend
      // fetches and submits it itself, nothing to buffer or re-upload
      // here.
      const uploadResponse = await uploadOCRRatedAge({
        msaId,
        costProjectionId,
        documentId: selectedDocumentId,
      }).unwrap();

      // RatedAgeOCRWatcher keeps watching this job after the modal closes,
      // and toasts once parsing finishes.
      dispatch(addProcessingRatedAgeOCRJob(uploadResponse.job_id));

      KaboodleToaster.success(
        uploadResponse.message || "Document submitted for OCR analysis",
      );

      setSelectedDocumentId(null);
      onClose();
    } catch (error) {
      // error is untyped on purpose since its shape depends on how
      // unwrap() failed, but the optional chaining below falls
      // through safely even for a non-Error throw.
      KaboodleToaster.error(
        error?.data?.error ||
          error?.message ||
          "Failed to submit document. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedDocumentId(null);
    onClose();
  };

  const isBusy = isLoading || isSubmitting;

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <div className="flex jc-c">
          <Button
            onClick={handleUpload}
            disabled={
              isBusy || !selectedDocumentId || !hasTarget || isAlreadyProcessing
            }
          >
            {isBusy ? "Submitting..." : "Submit"}
          </Button>
          <Button onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading="Process Rated Age Response">
        <div className="OCRRatedAgeModal">
          <div className="data-values">
            {isAlreadyProcessing ? (
              <p>
                A rated age response is already being processed for this file.
                Please wait for it to finish before submitting another.
              </p>
            ) : isDocumentsLoading ? (
              <div>Loading documents...</div>
            ) : !fileIdString ? (
              <div>File ID is required to load documents.</div>
            ) : filteredDocuments.length === 0 ? (
              <p>
                No rated age response documents found for this file. Upload one
                to the documents drawer as a Rated Age document first.
              </p>
            ) : (
              <>
                <label>Rated Age Response</label>
                <div className="ocr-document-list">
                  <div className="ocr-document-row ocr-document-row--header">
                    <span />
                    <span>Document</span>
                    <span>Uploaded</span>
                  </div>
                  {filteredDocuments.map((doc) => (
                    <label
                      key={doc.id}
                      className="ocr-document-row"
                      htmlFor={`ocr-document-${doc.id}`}
                    >
                      <input
                        type="radio"
                        id={`ocr-document-${doc.id}`}
                        name="ocr-rated-age-document"
                        checked={selectedDocumentId === doc.id}
                        onChange={() => setSelectedDocumentId(doc.id)}
                      />
                      <span>
                        {doc.original_filename ||
                          doc.original_filename_as_uploaded}
                      </span>
                      <span>{getDateFromISOStringDatePart(doc.created)}</span>
                    </label>
                  ))}
                </div>
                <p>
                  The selected document will be submitted to OCR for analysis.
                </p>
              </>
            )}
          </div>
        </div>
      </Card>
    </KaboodleModal>
  );
};

export default OCRRatedAgeModal;
